import React, {  useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  ArrowRight, 
  CheckCircle, 
  TrendingUp, 
  Users, 
  Clock,
  Shield,
  Eye,
  Play,
  Activity,
  UserCheck,
  Clipboard,
  Stethoscope
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';

const HealthcarePage: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  
 

  const challenges = [
    'Shortages in healthcare staffing and administrative inefficiencies',
    'Managing patient safety in high-risk environments',
    'Increased risk of exposure to hazards without proper PPE tracking',
    'Strict regulatory and compliance demands (e.g., health safety protocols, HR audits)',
    'Lack of real-time data on personnel performance or workplace compliance',
    'Difficulty in integrating disparate health information systems across departments and facilities'
  ];

  const solutions = [
    {
      name: 'GeneSeqer',
      path: '/products/ai/geneseqer',
      description: 'High-throughput genomics platform for rapid variant discovery and annotation.',
      benefits: ['Quality control', 'Functional annotation', 'Variant calling']
    },
    {
      name: 'Panaceon',
      path: '/products/ai/Panaceon',
      description: 'Unified health intelligence to spot risks early and guide better decisions.',
      benefits: ['Personalized therapeutics', 'Care orchestration', 'Predictive screening']
    },
    // {
    //   name: 'ManageRTC',
    //   path: '/products/it#ManageRTC',
    //   description: 'Advanced workforce management for healthcare organizations with compliance and safety features.',
    //   benefits: ['Staff Management', 'Compliance Monitoring', 'Performance Analytics']
    // },
    // {
    //   name: 'Websites',
    //   path: '/products/it#websites',
    //   description: 'Custom healthcare platforms and patient portals for medical institutions and practices.',
    //   benefits: ['Patient Portals', 'Healthcare Platforms', 'Telehealth Integration']
    // }
  ];

  const keyFeatures = [
    {
      icon: Shield,
      title: 'Safety-First Technology',
      description: 'Advanced monitoring systems that prioritize patient and staff safety with real-time compliance tracking.'
    },
    {
      icon: Clipboard,
      title: 'Compliance Without Complexity',
      description: 'Streamlined regulatory compliance management that simplifies adherence to healthcare standards.'
    },
    {
      icon: Users,
      title: 'Streamlined Workforce Operations',
      description: 'Efficient staff management and scheduling systems designed for healthcare operational demands.'
    },
    {
      icon: Activity,
      title: 'Tailored for Healthcare',
      description: 'Solutions specifically designed to meet the unique challenges and requirements of healthcare environments.'
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Patient Safety Enhancement',
      description: 'Advanced monitoring and compliance systems significantly improve patient safety outcomes and reduce incidents.',
      metric: '75% safety improvement'
    },
    {
      icon: Clock,
      title: 'Operational Efficiency',
      description: 'Streamlined workflows and automated processes reduce administrative overhead and improve care delivery.',
      metric: '50% efficiency gain'
    },
    {
      icon: UserCheck,
      title: 'Staff Compliance',
      description: 'Real-time monitoring ensures consistent adherence to safety protocols and regulatory requirements.',
      metric: '90% compliance rate'
    },
    {
      icon: TrendingUp,
      title: 'Quality of Care',
      description: 'Integrated systems and data-driven insights enhance overall quality of patient care and outcomes.',
      metric: '65% quality improvement'
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-4 h-4 bg-emerald-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 6,
                delay: i * 0.4,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-linear-to-br from-emerald-500 to-teal-600 neon-glow">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-emerald-400 uppercase tracking-wide">Smart Healthcare Technology</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    Healthcare <span className="bg-linear-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">Industry</span>
                  </h1>
                </div>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-2xl font-semibold mb-4 text-emerald-300"
              >
                Innovating Healthcare Through Intelligent Systems
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed"
              >
                The healthcare industry is at a turning point—balancing rising patient demands, workforce challenges, and the need 
                for data-driven care. Digital transformation has become essential in improving efficiency, safety, and patient outcomes. 
                At amasQIS.ai, we empower healthcare providers with AI, IT, and IoT solutions that simplify operations, strengthen 
                compliance, and support frontline workers.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-linear-to-r from-emerald-500 to-teal-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-emerald-500/25 transition-all duration-300"
                >
                  Enhance Healthcare
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative perspective-1000"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                  className="absolute -inset-10 bg-linear-to-r from-emerald-500 to-teal-600 opacity-20 rounded-full blur-3xl"
                />
                
                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5, rotateX: 5 }}
                  transition={{ duration: 0.4 }}
                  className="relative z-10 glass-strong rounded-2xl overflow-hidden transform-gpu"
                >
                  <ImageWithFallback
                    src='/assets/Industries/health-care.png'
                    alt="Healthcare Industry"
                    className="w-full h-96 object-cover"
                  />
                  
                  <div className="absolute inset-0 bg-linear-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center gap-3 text-white">
                        <div className="p-2 bg-emerald-500/20 rounded-lg backdrop-blur-sm">
                          <Heart className="w-5 h-5 text-emerald-500" />
                        </div>
                        <div>
                          <p className="font-semibold">AI-Powered</p>
                          <p className="text-sm text-white/80">Healthcare Solutions</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  className="absolute -top-4 -right-4 glass p-3 rounded-xl"
                >
                  <div className="flex items-center gap-2 text-emerald-500">
                    <Stethoscope className="w-4 h-4 animate-pulse" />
                    <span className="text-xs font-mono">HEALTH</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Challenges in the Healthcare Sector
            </h2>
            <p className="text-lg text-muted-foreground  text-center max-w-3xl mx-auto">
              Healthcare providers face mounting pressure across all levels of operations, requiring innovative solutions for safety and efficiency.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {challenges.map((challenge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
              >
                <div className="flex items-start gap-3">
                  <Activity className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                  <p className="text-muted-foreground">{challenge}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Our Solutions for Healthcare
            </h2>
            <p className="text-lg text-muted-foreground  text-center max-w-3xl mx-auto">
              Comprehensive healthcare solutions designed to improve patient safety, operational efficiency, and regulatory compliance.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <Link
                key={solution.name}
                to={solution.path}
                className="block group h-full"
              >
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300 cursor-pointer h-full flex flex-col"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-semibold text-emerald-400 group-hover:text-emerald-300 transition-colors">
                      {solution.name}
                    </h3>
                    <ArrowRight className="w-5 h-5 text-emerald-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="text-muted-foreground mb-4">{solution.description}</p>
                  <div className="space-y-2">
                    {solution.benefits.map((benefit) => (
                      <div key={benefit} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-teal-400" />
                        <span className="text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Why Healthcare Institutions Trust amasQIS.ai
            </h2>
            <p className="text-lg text-muted-foreground  text-center max-w-3xl mx-auto">
              amasQIS.ai bridges the gap between frontline care and back-office efficiency with intelligent, user-friendly systems.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300 text-center"
                >
                  <div className="mb-4 flex justify-center">
                    <Icon className="w-8 h-8 text-emerald-500" />
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Measurable Healthcare Impact
            </h2>
            <p className="text-lg text-muted-foreground  text-center max-w-3xl mx-auto">
              Transform your healthcare operations with quantifiable improvements in patient safety, efficiency, and care quality.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-emerald-500/20">
                      <Icon className="w-6 h-6 text-emerald-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                      <p className="text-muted-foreground mb-3">{benefit.description}</p>
                      <div className="text-teal-400 font-semibold">{benefit.metric}</div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-15">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Revolutionize Healthcare?
            </h2>
            <p className="text-lg text-muted-foreground  text-center mb-8">
              Join leading healthcare institutions that trust amasQIS.ai to deliver safer, smarter, and more efficient healthcare solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-linear-to-r from-emerald-500 to-teal-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-emerald-500/25 transition-all duration-300"
              >
                Enhance Healthcare
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300"
              >
                Explore Solutions
                <Eye className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export { HealthcarePage };
